import { ArrowDownUp, CheckCircle2, RotateCcw, Lock } from "lucide-react";
import {
  PageHeading, Section, Steps, StatusBadge, RoleBadge, STATUS_FLOW, Callout,
  Diagram, MockTable,
} from "../shared";

export default function LifecycleSection() {
  return (
    <div data-testid="guide-lifecycle">
      <PageHeading
        icon={ArrowDownUp}
        title="Action Item Lifecycle"
        description="Every action item travels the same road, from the moment it's created to the moment it's closed. Each stop has a purpose, and the system keeps everyone on the road in the right order so no step gets skipped."
      />

      <Callout variant="info" title="In plain words">
        An "action item" is one piece of work the system tracks. Its <span className="text-white">status</span> is just a label saying where it is on its journey — like a parcel that goes from "registered" to "out for delivery" to "delivered". The system makes sure the labels change in a sensible order.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Read this when you want to understand the big picture — what all the status labels mean and how an item gets from start to finish. You don't change anything here; the actual buttons live on the item's detail page and the Updates page.
        </p>
      </Section>

      <Section title="The journey, start to finish">
        <Diagram
          title="An action item's road from created to closed"
          frameLabel="Action Item lifecycle"
          legend={[
            <><span className="text-white">Registered</span> — an admin logs the item: what to do, who owns it, its lane, priority, and so on.</>,
            <><span className="text-white">In Progress</span> — the owner does the work, adds progress notes, and can attach sub-tasks.</>,
            <><span className="text-white">Review</span> — if needed, the item passes through SDF MD review (and sometimes EDGE MD review). This can't be skipped.</>,
            <><span className="text-white">Approval & Dispatch</span> — for above-MD lanes the item waits in Awaiting Dispatch; other lanes go straight towards closing.</>,
            <><span className="text-white">Sent Upward</span> — the PMO marks it dispatched, recording who it went to and when.</>,
            <><span className="text-white">Closed</span> — a closing note is written and the item is finished. Every step is saved in the history.</>,
          ]}
        >
          <MockTable
            columns={["Stop on the road", "Who acts", "What happens"]}
            rows={[
              [<span className="inline-flex"><StatusBadge status="REGISTERED" /></span>, <span className="inline-flex"><RoleBadge role="admin" /></span>, "An admin registers the item (can also be done in bulk from Meeting Tasks)."],
              [<span className="inline-flex"><StatusBadge status="IN_PROGRESS" /></span>, "Owner", "Work is underway; progress notes and sub-tasks get added."],
              [<span className="inline-flex"><StatusBadge status="WITH_SDF_MD_REVIEW" /></span>, <span className="inline-flex"><RoleBadge role="admin" /></span>, "Review path set by the lane — cannot be bypassed."],
              [<span className="inline-flex"><StatusBadge status="AWAITING_DISPATCH" /></span>, "PMO / Board Sec", "Above-MD items wait here after MD approval."],
              [<span className="inline-flex"><StatusBadge status="SENT_UPWARD" /></span>, "PMO", "Marked dispatched with a recipient and date."],
              [<span className="inline-flex"><StatusBadge status="CLOSED" /></span>, <span className="inline-flex"><RoleBadge role="admin" /></span>, "A closing note is written; the item is done."],
            ]}
          />
        </Diagram>
        <p className="text-xs text-white/40 italic">
          At any point before closing, an item can be cancelled. Reviewers can also send an item back to In Progress with a "Return for Amendments" note.
        </p>
      </Section>

      <Section title="All 11 status labels explained">
        <p className="text-sm text-white/65 leading-relaxed mb-2">
          Each row is a label an item can wear. Only admins can change the label, and the system blocks any change that doesn't make sense.
        </p>
        <div className="space-y-2">
          {STATUS_FLOW.map((s) => (
            <div key={s.key} className="flex items-start gap-3 py-2 px-3 rounded-md bg-white/[0.02]" data-testid={`text-status-${s.key}`}>
              <StatusBadge status={s.key} />
              <p className="text-xs text-white/60 leading-relaxed pt-0.5">{s.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="A few important rules">
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
            <p className="text-xs text-white/55"><span className="text-white font-medium">Closing:</span> the green Close Task button only appears once sign-off is granted (or if sign-off isn't required). A closing note is always required.</p>
          </div>
          <div className="flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
            <p className="text-xs text-white/55"><span className="text-white font-medium">End-of-the-road labels:</span> Closed, Sent Upward, and Cancelled are final. Once an item reaches one, its workflow buttons disappear and it moves to the Completed tab on Origins.</p>
          </div>
          <div className="flex items-start gap-2">
            <RotateCcw className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
            <p className="text-xs text-white/55"><span className="text-white font-medium">Return for Amendments:</span> from any review or later stage, a reviewer can send an item back to In Progress with a required reason. That note shows up as an orange banner on the assignee's Updates page.</p>
          </div>
        </div>
        <Callout variant="info">
          Your Super Admin can add extra restrictions on <span className="text-white">who</span> can move items into certain statuses. See <span className="text-white">Admin → Transition Permissions</span>.
        </Callout>
      </Section>
    </div>
  );
}
